import { DeleteButton } from "@constellation-academy/epic-ui";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useMutation, usePaginationFragment } from "react-relay";
import { useNavigate } from "react-router-dom";
import { VerticalContainerSpacingWrapper } from "@corestyle/component-theme/default-spacings.component";
import { useHasPermissions } from "@hooks/use-has-permissions";
import { useTranslation } from "@i18n/hooks";
import { type instructorsTable_DeleteMutation } from "@relay/instructorsTable_DeleteMutation.graphql";
import { type instructorsTable_InstructorsListFragment$key } from "@relay/instructorsTable_InstructorsListFragment.graphql";
import { type instructorsTable_Refetch } from "@relay/instructorsTable_Refetch.graphql";
import { Paths } from "@routes/paths";
import {
	DELETE_INSTRUCTOR_MUTATION,
	INSTRUCTORS_TABLE_FRAGMENT,
} from "./instructors-table.graphql";
import { ActionButtonsWrapper, ButtonContainer } from "./instructors-table.styles";
import { type InstructorsTableProps } from "./instructors-table.types";

export const InstructorsTable = ({ instructorsFragmentRef }: InstructorsTableProps) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const {
		data: Admin,
		hasPrevious,
		hasNext,
		loadPrevious,
		loadNext,
	} = usePaginationFragment<
		instructorsTable_Refetch,
		instructorsTable_InstructorsListFragment$key
	>(INSTRUCTORS_TABLE_FRAGMENT, instructorsFragmentRef);
	const instructors = Admin.Admin.Instructor.InstructorImpls.edges;
	const [deleteInstructor, isDeleting] = useMutation<instructorsTable_DeleteMutation>(
		DELETE_INSTRUCTOR_MUTATION,
	);

	const canCreate = useHasPermissions(["UserInAccountPermission_Instructors_CreateInstructors"]);
	const canUpdate = useHasPermissions(["UserInAccountPermission_Instructors_UpdateInstructors"]);
	const canDelete = useHasPermissions(["UserInAccountPermission_Instructors_DeleteInstructors"]);

	const onDelete = (ids: string[]) => {
		deleteInstructor({
			variables: {
				input: {
					ids,
				},
				connections: [Admin.Admin.Instructor.InstructorImpls.__id],
			},
		});
	};

	function handleOnClick() {
		navigate(Paths.instructors.withId("new").edit.path);
	}

	return (
		<>
			{canCreate && (
				<ButtonContainer>
					<Button
						onClick={() => {
							handleOnClick();
						}}
					>
						{t("instructor.new")}
					</Button>
				</ButtonContainer>
			)}
			<VerticalContainerSpacingWrapper>
				<DataTable
					emptyMessage={t("instructor.noInstructors")}
					value={instructors?.map((b) => b!.node!) as any[]}
				>
					<Column header={t("table.name")} field={"name"} />
					<Column header={t("instructor.position")} field={"position"} />

					{(canUpdate || canDelete) && (
						<Column
							header={t("table.actions")}
							style={{ width: "20%" }}
							body={(item) => (
								<ActionButtonsWrapper>
									{canUpdate && (
										<Button
											onClick={() => {
												navigate(
													Paths.instructors.withId(item.superId).edit
														.path,
												);
											}}
											icon={"pi pi-pencil"}
										/>
									)}

									{canDelete && (
										<DeleteButton
											disabled={isDeleting}
											onDelete={onDelete}
											singularName={t("instructor.trainer")}
											pluralName={t("instructor.trainers")}
											value={[item.id]}
										/>
									)}
								</ActionButtonsWrapper>
							)}
						/>
					)}
				</DataTable>
			</VerticalContainerSpacingWrapper>
			<div className="flex justify-content-center align-items-center">
				<Button
					disabled={!hasPrevious}
					onClick={() => loadPrevious(20)}
					className="mr-3 p-button-secondary"
				>
					{t("actions.back")}
				</Button>
				<Button
					className="p-button-secondary"
					disabled={!hasNext}
					onClick={() => loadNext(20)}
				>
					{t("actions.next")}
				</Button>
			</div>
		</>
	);
};
